import {
  buildAllColumnDefinitions,
  COLUMN_LAYOUT_CONFIG,
  getAgeGroupKeys,
  getCategoryKeysForRow,
} from "./columnLayouts";
import { parseExtraSections } from "./detailSections";
import { getLinksCell, parseLinks } from "./links";
import { getObjectiveRawValue, parseRowObjectives } from "./objectives";
import { toNumberOrNull, toText } from "./text";
import {
  UNCLASSIFIED_CODE,
  type AgeGroup,
  type Intervention,
  type InterventionDataset,
  type InterventionStatus,
  type ObjectiveValue,
  type SourceRow,
  type View,
} from "./types";
import { getViewCode } from "./views";

/** Workflow metadata stored next to `row_data` in the database. */
export interface InterventionRecordMeta {
  dbId: string;
  status: InterventionStatus;
  submittedBy: string | null;
  createdAt?: string | null;
  createdBy?: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  questionnaire?: { id: string; version: number } | null;
}

export interface InterventionRecord {
  row: SourceRow;
  meta?: InterventionRecordMeta;
}

export interface ParseContext {
  views: View[];
  ageGroups: AgeGroup[];
}

/**
 * True for rows that only carry an ID (traceability placeholders in the
 * source workbook) and hold no displayable content.
 */
function isTraceabilityOnlyRow(row: SourceRow, views: View[]): boolean {
  const fields = [
    "Title_NO",
    "Title_EN",
    "Description_NO",
    "Description_EN",
    "Arena",
    "Region",
    "Youngest",
    "Oldest",
  ];
  return (
    fields.every((field) => !toText(row[field])) &&
    !toText(getLinksCell(row)) &&
    !getObjectiveRawValue(row) &&
    views.every((view) => !toText(row[view.column]))
  );
}

/** Parses source rows (Excel rows or database records) into a dataset. */
export function parseInterventions(
  records: InterventionRecord[],
  context: ParseContext,
): InterventionDataset {
  const { views, ageGroups } = context;
  const sourceRows = records.map((record) => record.row);
  const columnDefsByLayout = buildAllColumnDefinitions(sourceRows, ageGroups);
  const objectives: Record<string, ObjectiveValue> = {};
  const regions = new Set<string>();
  const availableViewCodes: Record<string, Set<string>> = Object.fromEntries(
    views.map((view) => [view.id, new Set<string>()]),
  );
  const itemsById = new Map<string, Intervention>();

  for (const { row, meta } of records) {
    const itemNumber = toText(row.ID);
    if (!itemNumber || isTraceabilityOnlyRow(row, views)) continue;

    const titleNo = toText(row.Title_NO);
    const descriptionNo = toText(row.Description_NO);
    const region = toText(row.Region);
    regions.add(region);

    const codes: Record<string, string> = {};
    const raw: Record<string, string> = {};
    for (const view of views) {
      const value = toText(row[view.column]);
      const code = getViewCode(view, value);
      raw[view.id] = value;
      codes[view.id] = code;
      availableViewCodes[view.id]!.add(code && view.values[code] ? code : UNCLASSIFIED_CODE);
    }

    const youngest = toNumberOrNull(row.Youngest);
    const oldest = toNumberOrNull(row.Oldest);
    const ageGroupKeys = getAgeGroupKeys(ageGroups, youngest, oldest);
    const columnLayoutKeys = Object.fromEntries(
      Object.entries(COLUMN_LAYOUT_CONFIG.layouts).map(([layoutId, layout]) => [
        layoutId,
        layout.type === "ageGroups"
          ? ageGroupKeys
          : getCategoryKeysForRow(row, layout, columnDefsByLayout[layoutId] ?? [], codes),
      ]),
    );

    const id = meta?.dbId ?? `Tiltak ${itemNumber}`;
    itemsById.set(id, {
      id,
      itemNumber,
      display: { no: titleNo, en: toText(row.Title_EN) || titleNo },
      description: { no: descriptionNo, en: toText(row.Description_EN) || descriptionNo },
      codes,
      raw,
      objectiveCodes: parseRowObjectives(row, objectives),
      links: parseLinks(getLinksCell(row)),
      extraSections: parseExtraSections(row),
      arena: toText(row.Arena),
      region,
      youngest,
      oldest,
      ageGroupKeys,
      columnLayoutKeys,
      sourceRow: row,
      ...(meta && {
        dbId: meta.dbId,
        status: meta.status,
        submittedBy: meta.submittedBy,
        createdAt: meta.createdAt,
        createdBy: meta.createdBy,
        updatedAt: meta.updatedAt,
        updatedBy: meta.updatedBy,
        questionnaire: meta.questionnaire ?? null,
      }),
    });
  }

  return {
    items: [...itemsById.values()],
    regions: [...regions],
    objectives,
    availableViewCodes,
    columnDefsByLayout,
    sourceRows,
  };
}

/**
 * Draft and submitted (not yet approved) interventions are excluded from the
 * matrix and the statistics for everyone; they only appear in the list.
 */
export function isPublished(item: Intervention): boolean {
  return item.status !== "draft" && item.status !== "submitted";
}
